import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchPropertyResearchLandingPageData } from '../../common/helpers/data/landingPages/fetchPropertyResearchLandingPageData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import { LandingPageTypes } from '../../common/helpers/domain/landingPages';
import {
  getSubcategories,
  PostMainCategory,
} from '../../common/helpers/domain/postCategories';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getPropertyTypeTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generatePostMainCategoryBreadcrumbList } from '../../components/landingPage/breadcrumb';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
  DynamicComponentForInPageSearch,
  DynamicComponentForLocationRecommendation,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { PropertyTypeTabs } from '../../components/propertyTypeTabs/PropertyTypeTabs';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { RecommendedLocation } from '../../queries/homePage/locationRecommendation';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import ViewMoreLink from '../../components/ViewMoreLink/ViewMoreLink';
import PostListModule from '../../components/landingPage/PostListModule';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { WholeState } from '../../store/common/type';
import {
  trackArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackHeroArticle,
  trackHeroVideoArticle,
  trackMostPopularArticle,
  trackPropertyTypeTab,
  trackRecommendedLocationCard,
  trackRecommendedLocationCarouselArrow,
  trackViewMoreLink,
} from '../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface PropertyResearchLandingPageProps {
  canonicalLink: string;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  reviewPosts: Content[];
  previewPosts: Content[];
  povPosts: Content[];
  heroVideo: HeroVideoInfo;
  featuredVideos: Content[];
  recommendedLocations: RecommendedLocation[];
}

function PropertyResearchLandingPage({
  canonicalLink,
  heroArticle,
  popularArticles,
  reviewPosts,
  previewPosts,
  povPosts,
  heroVideo,
  featuredVideos,
  recommendedLocations,
}: PropertyResearchLandingPageProps) {
  const moduleResource = commonResource.postModules.propertyResearch;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const analyticsSource = generateEventSourceByPageType(
    LandingPageTypes.ProjectResearchPage,
  );

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader
        pageType={LandingPageTypes.ProjectResearchPage}
        canonicalLink={canonicalLink}
      />
      <Breadcrumb
        items={generatePostMainCategoryBreadcrumbList(
          PostMainCategory.ProjectResearch,
        )}
      />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title.propertyResearch}
          subtitle={homeResource.subtitle.propertyResearch}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(popularArticle) =>
                trackMostPopularArticle(
                  userInfo,
                  popularArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(LandingPageTypes.ProjectResearchPage)}
        />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={commonResource.propertyTypeTabs.title} />
        <PropertyTypeTabs
          onClickTabCard={(tabArticle) => {
            trackPropertyTypeTab(userInfo, analyticsSource, tabArticle);
          }}
        />
      </Row>
      {reviewPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="review"
        >
          <PostListModule
            title={moduleResource.review.title}
            subtitle={moduleResource.review.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={LandingPageTypes.ReviewPage}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    generateEventSourceByPageType(targetLandingPage),
                    ANALYTICS_FEATURES.PROJECT_REVIEW,
                  );
                }}
              />
            }
          >
            <PostList
              contents={reviewPosts}
              adUnit={AdUnits.HP_Pos1}
              adSect={getAdSectByPageType(LandingPageTypes.ProjectResearchPage)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.PROJECT_REVIEW,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {recommendedLocations.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle
            title={homeResource.locationRecommendation.title}
            subtitle={homeResource.locationRecommendation.subtitle}
          />
          <DynamicComponentForLocationRecommendation
            locations={recommendedLocations}
            onClickRecommendedLocationCarouselArrow={() =>
              trackRecommendedLocationCarouselArrow(
                userInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_HIGHLIGHT,
              )
            }
            onClickRecommendedLocationCard={(locationInfo) =>
              trackRecommendedLocationCard(
                userInfo,
                locationInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_HIGHLIGHT,
              )
            }
          />
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <DynamicComponentForInPageSearch />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <BillboardAd
          adUnit={AdUnits.HP_Billboard2}
          sect={getAdSectByPageType(LandingPageTypes.ProjectResearchPage)}
        />
      </Row>
      {(heroVideo || featuredVideos.length > 0) && (
        <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
          <div style={{ overflowX: 'hidden' }}>
            <DynamicComponentForHeroVideo
              heroVideo={heroVideo}
              onClick={(heroVideo) =>
                trackHeroVideoArticle(
                  userInfo,
                  heroVideo,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_VIDEO,
                )
              }
            />
            <DynamicComponentForFeaturedVideos
              contents={featuredVideos}
              onClickVideoCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.VIDEO,
                )
              }
            />
          </div>
        </Row>
      )}
      {previewPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="preview"
        >
          <PostListModule
            title={moduleResource.preview.title}
            subtitle={moduleResource.preview.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={LandingPageTypes.PreviewPage}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    generateEventSourceByPageType(targetLandingPage),
                    ANALYTICS_FEATURES.PROJECT_PREVIEW,
                  );
                }}
              />
            }
          >
            <PostList
              contents={previewPosts}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.PROJECT_PREVIEW,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {povPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="point-of-view"
        >
          <PostListModule
            title={moduleResource.pov.title}
            subtitle={moduleResource.pov.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={LandingPageTypes.PointOfViewPage}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    generateEventSourceByPageType(targetLandingPage),
                    ANALYTICS_FEATURES.POINT_OF_VIEW,
                  );
                }}
              />
            }
          >
            <PostList
              contents={povPosts}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.POINT_OF_VIEW,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getPropertyTypeTabDefinition(
            getSubcategories(PostMainCategory.ProjectResearch),
          )}
          excludePostIds={extractPostIds(
            [...reviewPosts, ...previewPosts, ...povPosts, ...featuredVideos],
            heroVideo,
          )}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(LandingPageTypes.ProjectResearchPage)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

PropertyResearchLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
}: CustomNextPageContext): Promise<PropertyResearchLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  try {
    const { heroArticle, mostPopular, recommendedLocations } =
      await fetchLandingPageTopContentData(apolloClient, {
        postCategories: getSubcategories(PostMainCategory.ProjectResearch),
      });

    const { pointOfView, preview, review, video } =
      await fetchPropertyResearchLandingPageData(
        apolloClient,
        heroArticle?.id ? [heroArticle.id] : [],
      );

    const landingPageType = LandingPageTypes.ProjectResearchPage;
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    return {
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
      reviewPosts: review,
      previewPosts: preview,
      povPosts: pointOfView,
      heroVideo: video.heroVideo,
      featuredVideos: video.featuredVideos,
      recommendedLocations,
    };
  } catch (e) {
    console.log('Error when fetch homepage article and posts:', e);
  }
};

export default PropertyResearchLandingPage;
